import PropTypes from 'prop-types'
import { memo, useMemo } from 'react'
import { useUserContext } from 'app/services/UserProvider/UserProvider'
import Button from 'components/core/Button/Button'
import Modal from 'components/interface/Modal/Modal'
import { useModalContext } from 'components/interface/Modal/provider/ModalProvider'
import UserLogin from 'components/interface/UserLogin/UserLogin'

/**
 * The `ModalSignIn` handles the rendering of
 * the sign up modal
 *
 * @param {object} props - the component props
 * @returns {React.ReactElement} the element
 */
function ModalSignIn (props) {
  const { completeAction, completeLabel, isSignUp } = props

  const { account } = useUserContext()
  const { onAction } = useModalContext()
  const title = useMemo(() => {
    if (account.hasAccountLoginError) {
      return <h1>Unable to Connect</h1>
    }

    if (!account.hasWallet) {
      return <h1>{isSignUp ? 'Sign Up' : 'Sign In'}</h1>
    }

    if (!account.isSignedIn) {
      if (account.isPassportHolder) {
        // Alternate title for passport holders
        return <h1>Welcome, Passport Holder</h1>
      }

      return <h1>Get Access</h1>
    }

    if (account?.user?.firstName) {
      return <h1>Welcome back, {account.user.firstName}!</h1>
    }

    return <h1>Welcome!</h1>
  }, [account, isSignUp])

  /**
   * Memo: The footer options
   */
  const options = useMemo(() => {
    if (account?.isSignedIn) {
      return (
        <>
          <li>
            <Button size="large" color="b" onClick={() => onAction('close')}>
              Close
            </Button>
          </li>
          <li>
            <Button
              size="large"
              color="a"
              onClick={() => onAction(completeAction)}
              useHighlight={true}
            >
              {completeLabel}
            </Button>
          </li>
        </>
      )
    }

    return (
      <>
        <li>
          <Button
            type="symbol"
            icon={'close'}
            size="large"
            color="b"
            onClick={() => onAction('close')}
          />
        </li>
      </>
    )
  }, [account, completeAction, completeLabel])

  return (
    <Modal.Container size="x-small">
      <Modal.Header>{title}</Modal.Header>
      <Modal.Body>
        <UserLogin isSignUp={isSignUp} />
      </Modal.Body>
      <Modal.Footer options={[]}>{options}</Modal.Footer>
    </Modal.Container>
  )
}

/** @type {object} */
ModalSignIn.propTypes = {
  accessId: PropTypes.string,
  completeAction: PropTypes.string,
  completeLabel: PropTypes.string,
  isSignUp: PropTypes.bool
}

/** @type {object} */
ModalSignIn.defaultProps = {
  accessId: null,
  completeAction: 'view_account',
  completeLabel: 'View Account',
  isSignUp: false
}

// Memoize
export default memo(ModalSignIn)
